import type { AnyFunction } from '../mod.ts';

export interface DebouncedFunction<T extends AnyFunction> {
  (this: ThisParameterType<T>, ...args: Parameters<T>): void;

  /** Cancels registered future calls if exists. */
  cancel: VoidFunction;
}

/**
 * Simplest implementation of "debounce" function.
 * Returns function wrapper that delays original function call
 * until timeout since last call will be reached.
 * Similar to `debounce` from lodash/underscore.
 *
 * @param fn Function.
 * @param timeout Timeout in milliseconds.
 * @returns Debounced function.
 */
export function debounce<T extends AnyFunction>(fn: T, timeout: number): DebouncedFunction<T> {
  let timerId: ReturnType<typeof setTimeout>;

  const debounced: DebouncedFunction<T> = function (...args) {
    clearTimeout(timerId);

    timerId = setTimeout(() => fn.apply(this, args), timeout);
  };

  debounced.cancel = () => {
    clearTimeout(timerId);
  };

  return debounced;
}
